// convert.ts — the ONE place where UI floats (euros, percent) cross into the engine's
// integer cents / basis points, and back. Everything on the form side of this module is
// a plain JS number in € or %; everything on the API side is an integer per D-003.
import { roundCentsHalfAwayFromZero } from '@etf-cockpit/engine';

/** € float -> integer cents, rounded half away from zero (kaufmännisch, matches D-003). */
export function eurToCents(eur: number): number {
  return roundCentsHalfAwayFromZero(eur * 100);
}

/** Integer cents -> € float (exact; division by 100 never needs rounding in float64 for our ranges). */
export function centsToEur(cents: number): number {
  return cents / 100;
}

/** Percent float (27.5 for "27,5 %") -> integer basis points (2750), rounded half away from zero.
 * The single conversion used for every %<->bp field at the API boundary, including
 * contributionStepUpPercent -> ForecastConfig/BacktestConfig.contributionStepUpBp
 * (Sparplan-Dynamik) — deliberately not reimplemented per-field. */
export function percentToBp(percent: number): number {
  return roundCentsHalfAwayFromZero(percent * 100);
}

/** Integer basis points -> percent float (2750 -> 27.5). */
export function bpToPercent(bp: number): number {
  return bp / 100;
}

/**
 * Converts an absolute "YYYY-MM" picked month (Sonderzahlungen's Monat/Jahr picker,
 * `<input type="month">`) into the forecast driver's 0-based `monthIndex` relative to
 * the scenario's `startDate` (YYYY-MM-DD) — the ONE place a year/month picker crosses
 * into ForecastConfig.extraContributions[].monthIndex. Month 0 is the plan's own start
 * month (forecast.ts: `addMonths(startDate, 0) === startDate`). Can return a negative
 * number (a picked month before the plan starts) or a number >= months (on/after the
 * accumulation phase ends) — the engine ignores both (see ForecastConfig.extraContributions
 * doc), so this function does not clamp.
 */
export function monthIndexFromYearMonth(startDateIso: string, yearMonth: string): number {
  const [startYear, startMonth] = startDateIso.split('-').map(Number) as [number, number];
  const [targetYear, targetMonth] = yearMonth.split('-').map(Number) as [number, number];
  return (targetYear - startYear) * 12 + (targetMonth - startMonth);
}

/** Inverse of monthIndexFromYearMonth — a 0-based monthIndex back to "YYYY-MM", for
 * pre-filling the picker with a sane default (e.g. "add a row at today's plan month"). */
export function yearMonthFromMonthIndex(startDateIso: string, monthIndex: number): string {
  const [startYear, startMonth] = startDateIso.split('-').map(Number) as [number, number];
  const total = startYear * 12 + (startMonth - 1) + monthIndex;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}`;
}
